import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabase'

const ANTHROPIC_MODEL = 'claude-sonnet-4-5'

function todayStr() { return new Date().toISOString().slice(0, 10) }

function buildSystemPrompt(data) {
  const { clients, staff, compat, jobs, rules } = data
  const clientList = clients.map(c => {
    const prefs = []
    if (c.address) prefs.push(`dirección: ${c.address}`)
    if (c.city) prefs.push(`ciudad: ${c.city}`)
    if (c.frequency) prefs.push(`frecuencia: ${c.frequency}`)
    const incompatible = Object.entries(compat[c.id] || {}).filter(([,l])=>l==='incompatible').map(([sid])=>staff.find(s=>s.id===sid)?.name).filter(Boolean)
    if (incompatible.length) prefs.push(`NO puede limpiar: ${incompatible.join(', ')}`)
    const preferred = Object.entries(compat[c.id] || {}).filter(([,l])=>l==='preferred').map(([sid])=>staff.find(s=>s.id===sid)?.name).filter(Boolean)
    if (preferred.length) prefs.push(`prefiere: ${preferred.join(', ')}`)
    return `- ${c.name||c.nickname} (${prefs.join(' | ')||'sin preferencias'})`
  }).join('\n')
  const staffList = staff.map(s=>`- ${s.name}`).join('\n')
  const todayJobs = jobs.map(j=>{
    const cl=clients.find(c=>c.id===j.client_id)
    return `- ${cl?.name||cl?.nickname||'?'} a las ${j.scheduled_time||'?'}, ruta ${j.route||'sin asignar'}`
  }).join('\n')
  const rulesText = rules.length ? rules.map(r=>`- ${r.rule_text}`).join('\n') : 'Ninguna todavía.'
  return `Eres la asistente de programación de Kitty's Cleaning. Ayuda a Mami a organizar las rutas del día.\n\nHABLA SIEMPRE EN ESPAÑOL. Sé amable y directa.\n\n=== PERSONAL ===\n${staffList||'No hay personal.'}\n\n=== CLIENTES ===\n${clientList||'No hay clientes.'}\n\n=== TRABAJOS DE HOY ===\n${todayJobs||'No hay trabajos.'}\n\n=== REGLAS Y NOTAS IMPORTANTES (memorizadas) ===\n${rulesText}\n\nCuando Mami te diga algo importante sobre un cliente, empleada, o regla de trabajo, confírmale que lo guardaste. Agrupa clientes por zona. Respeta incompatibilidades. Si quiere cambiar algo, rearma el plan.`
}

export default function Asistente() {
  const [messages, setMessages] = useState([{role:'assistant',content:'¡Hola! 👋 Soy tu asistente de rutas. Puedo sugerir cómo organizar el día y aprender las reglas especiales del negocio.\n\n¿Quieres que empiece con los trabajos de hoy?'}])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [crmData, setCrmData] = useState({clients:[],staff:[],compat:{},jobs:[],rules:[]})
  const [dateStr, setDateStr] = useState(todayStr())
  const [showRules, setShowRules] = useState(false)
  const [newRule, setNewRule] = useState('')
  const bottomRef = useRef(null)

  useEffect(()=>{ loadData() },[dateStr])
  useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:'smooth'}) },[messages])

  async function loadData() {
    const [{data:cl},{data:st},{data:cp},{data:jb},{data:rl}] = await Promise.all([
      supabase.from('clients').select('*').eq('is_active',true).order('name'),
      supabase.from('staff').select('*').eq('is_active',true).order('name'),
      supabase.from('client_staff_compat').select('*'),
      supabase.from('jobs').select('*').eq('scheduled_date',dateStr),
      supabase.from('ai_rules').select('*').order('created_at',{ascending:false}),
    ])
    const compatMap={}
    ;(cp||[]).forEach(r=>{ if(!compatMap[r.client_id])compatMap[r.client_id]={}; compatMap[r.client_id][r.staff_id]=r.level })
    setCrmData({clients:cl||[],staff:st||[],compat:compatMap,jobs:jb||[],rules:rl||[]})
  }

  async function saveRule(text) {
    if (!text.trim()) return
    await supabase.from('ai_rules').insert({rule_text:text.trim()})
    loadData()
  }

  async function deleteRule(id) {
    await supabase.from('ai_rules').delete().eq('id',id)
    setCrmData(prev=>({...prev,rules:prev.rules.filter(r=>r.id!==id)}))
  }

  async function sendMessage(userText) {
    if (!userText.trim()||loading) return
    const userMsg={role:'user',content:userText}
    const newMessages=[...messages,userMsg]
    setMessages(newMessages)
    setInput('')
    setLoading(true)
    const learnKeywords=['recuerda que','anota que','no olvides que','guarda que']
    const isLearning=learnKeywords.some(k=>userText.toLowerCase().includes(k))
    try {
      const res = await fetch('/.netlify/functions/claude',{
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({
          model:ANTHROPIC_MODEL,
          max_tokens:1000,
          system:buildSystemPrompt(crmData),
          messages:newMessages.map(m=>({role:m.role,content:m.content})),
        }),
      })
      const data = await res.json()
      const reply = data.content?.map(b=>b.text||'').join('')
      if (reply) {
        setMessages(prev=>[...prev,{role:'assistant',content:reply}])
        if (isLearning) {
          await saveRule(userText)
          setMessages(prev=>[...prev,{role:'assistant',content:'📌 Guardé esa nota para recordarla siempre.'}])
        }
      } else {
        setMessages(prev=>[...prev,{role:'assistant',content:'Error: '+JSON.stringify(data.error||data)}])
      }
    } catch(e) {
      setMessages(prev=>[...prev,{role:'assistant',content:'Error de conexión: '+e.message}])
    }
    setLoading(false)
  }

  const T={primary:'#e8547a',light:'#fdeef3',border:'#e3e5e8',bg:'#f8f9fa'}

  return (
    <div style={{display:'flex',height:'100%',overflow:'hidden'}}>
      <div style={{width:210,borderRight:`1px solid ${T.border}`,display:'flex',flexDirection:'column',background:'#fff',flexShrink:0}}>
        <div style={{padding:'14px 14px 10px',borderBottom:`1px solid ${T.border}`}}>
          <div style={{fontSize:13,fontWeight:600,marginBottom:8}}>📅 Fecha</div>
          <input type="date" value={dateStr} onChange={e=>setDateStr(e.target.value)} style={{width:'100%',fontSize:12,padding:'6px 8px',border:`1px solid ${T.border}`,borderRadius:7,background:'#f8f9fa'}}/>
        </div>
        <div style={{padding:'12px 14px 8px',borderBottom:`1px solid ${T.border}`}}>
          <div style={{fontSize:12,fontWeight:600,color:'#80868b',marginBottom:6}}>PERSONAL ({crmData.staff.length})</div>
          {crmData.staff.map(s=>(
            <div key={s.id} style={{fontSize:12,color:'#5f6368',marginBottom:3,display:'flex',alignItems:'center',gap:5}}>
              <span style={{width:7,height:7,borderRadius:'50%',background:T.primary,display:'inline-block'}}/>
              {s.name}
            </div>
          ))}
        </div>
        <div style={{padding:'12px 14px 8px',flex:1,overflowY:'auto',borderBottom:`1px solid ${T.border}`}}>
          <div style={{fontSize:12,fontWeight:600,color:'#80868b',marginBottom:6}}>TRABAJOS HOY ({crmData.jobs.length})</div>
          {crmData.jobs.length===0
            ? <div style={{fontSize:11,color:'#bdc1c6'}}>No hay trabajos</div>
            : crmData.jobs.map(j=>{
              const cl=crmData.clients.find(c=>c.id===j.client_id)
              return <div key={j.id} style={{fontSize:11,color:'#5f6368',marginBottom:4,padding:'4px 7px',background:'#f1f3f4',borderRadius:6}}>
                <div style={{fontWeight:500}}>{cl?.name||cl?.nickname||'?'}</div>
                <div style={{color:'#80868b'}}>{j.scheduled_time||'Sin hora'} · {j.route?`Ruta ${j.route}`:'Sin ruta'}</div>
              </div>
            })
          }
        </div>
        <div style={{padding:'10px 14px'}}>
          <button onClick={()=>setShowRules(p=>!p)} style={{fontSize:11,color:'#80868b',background:'none',border:'none',cursor:'pointer',padding:0}}>
            📌 Notas guardadas ({crmData.rules.length}) {showRules?'▲':'▼'}
          </button>
          {showRules&&(
            <div style={{marginTop:8}}>
              {crmData.rules.map(r=>(
                <div key={r.id} style={{fontSize:11,color:'#5f6368',marginBottom:4,display:'flex',gap:4,alignItems:'flex-start'}}>
                  <span style={{flex:1,lineHeight:1.4}}>{r.rule_text}</span>
                  <button onClick={()=>deleteRule(r.id)} style={{color:'#bdc1c6',background:'none',border:'none',cursor:'pointer',fontSize:12,padding:0}}>×</button>
                </div>
              ))}
              <div style={{display:'flex',gap:4,marginTop:6}}>
                <input value={newRule} onChange={e=>setNewRule(e.target.value)}
                  placeholder="Agregar nota..."
                  style={{flex:1,fontSize:11,padding:'4px 7px',border:`1px solid ${T.border}`,borderRadius:6}}
                  onKeyDown={e=>{if(e.key==='Enter'){saveRule(newRule);setNewRule('')}}}/>
                <button onClick={()=>{saveRule(newRule);setNewRule('')}} style={{fontSize:11,padding:'4px 8px',background:T.primary,color:'#fff',border:'none',borderRadius:6,cursor:'pointer'}}>+</button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{padding:'14px 20px',borderBottom:`1px solid ${T.border}`,background:'#fff',display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:22}}>🤖</span>
          <div>
            <div style={{fontSize:14,fontWeight:600}}>Asistente de Rutas</div>
            <div style={{fontSize:11,color:'#80868b'}}>Habla en español · Aprende y recuerda · Sugiere rutas</div>
          </div>
          <div style={{marginLeft:'auto',display:'flex',gap:6}}>
            {[
              {label:'Organizar hoy',msg:`Tengo ${crmData.jobs.length} trabajos para el ${dateStr}. ¿Cómo organizarías las rutas?`},
              {label:'Sugerir rutas',msg:'Sugiere las rutas para hoy con el personal disponible, respetando las incompatibilidades.'},
              {label:'Optimizar orden',msg:'¿Cómo optimizarías el orden de visitas para manejar menos?'},
            ].map(s=>(
              <button key={s.label} onClick={()=>sendMessage(s.msg)}
                style={{fontSize:11,padding:'5px 10px',border:`1px solid ${T.border}`,borderRadius:20,background:'#fff',color:'#5f6368',cursor:'pointer',whiteSpace:'nowrap'}}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:'20px',display:'flex',flexDirection:'column',gap:14}}>
          {messages.map((m,i)=>(
            <div key={i} style={{display:'flex',justifyContent:m.role==='user'?'flex-end':'flex-start'}}>
              {m.role==='assistant'&&<div style={{width:28,height:28,borderRadius:'50%',background:T.light,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,marginRight:8,flexShrink:0}}>🤖</div>}
              <div style={{maxWidth:'72%',padding:'10px 14px',borderRadius:m.role==='user'?'18px 18px 4px 18px':'4px 18px 18px 18px',background:m.role==='user'?T.primary:'#fff',color:m.role==='user'?'#fff':'#202124',border:m.role==='user'?'none':`1px solid ${T.border}`,fontSize:13,lineHeight:1.6,whiteSpace:'pre-wrap'}}>
                {m.content}
              </div>
            </div>
          ))}
          {loading&&<div style={{display:'flex',alignItems:'center',gap:8}}>
            <div style={{width:28,height:28,borderRadius:'50%',background:T.light,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14}}>🤖</div>
            <div style={{padding:'10px 14px',borderRadius:'4px 18px 18px 18px',background:'#fff',border:`1px solid ${T.border}`,fontSize:13,color:'#80868b'}}>pensando...</div>
          </div>}
          <div ref={bottomRef}/>
        </div>
        <div style={{padding:'12px 20px',borderTop:`1px solid ${T.border}`,background:'#fff',display:'flex',gap:10,alignItems:'flex-end'}}>
          <textarea value={input} onChange={e=>setInput(e.target.value)}
            onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendMessage(input)}}}
            placeholder='Escribe aquí... (ej: "Chinita es una señora mayor" o "recuerda que Colony va primero")'
            rows={2}
            style={{flex:1,fontSize:13,padding:'10px 14px',border:`1px solid ${T.border}`,borderRadius:12,resize:'none',lineHeight:1.5,outline:'none',fontFamily:'inherit',background:'#f8f9fa'}}/>
          <button onClick={()=>sendMessage(input)} disabled={loading||!input.trim()}
            style={{padding:'10px 18px',background:input.trim()&&!loading?T.primary:'#e3e5e8',color:input.trim()&&!loading?'#fff':'#bdc1c6',border:'none',borderRadius:12,cursor:input.trim()&&!loading?'pointer':'default',fontSize:13,fontWeight:500}}>
            Enviar
          </button>
        </div>
      </div>
    </div>
  )
}
